import { useState, useEffect } from 'react';
import * as Yup from 'yup';
import {
  Table,
  TableHead,
  FormControl,
  TableBody,
  TextField,
  Paper,
  TableRow,
  TableCell,
  TableContainer,
  Button,
  Box,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {
  Formik,
  FieldArray,
  useField,
  Field,
  useFormikContext,
  setNestedObjectValues,
} from 'formik';
import { DebounceInput } from 'react-debounce-input';

const validationSchema = Yup.object({
  controls: Yup.array().of(
    Yup.object({
      id: Yup.string(),
      name: Yup.string().required('Required').nullable(),
      controlPrimerName: Yup.string().required('Required').nullable(),
    })
  ),
});

const ControlTable = ({ controlActions, getWellName }) => {
  const errorStyle = {
    color: 'red',
    marginTop: '5px',
  };

  const ErrorMessage = ({ name }) => {
    const [, meta] = useField(name);
    return meta.touched && meta.error ? (
      <Box sx={errorStyle}>{meta.error}</Box>
    ) : null;
  };

  // used to validate as soon as a new control is added to show errors
  const FormObserver = () => {
    const { values, initialValues, validateForm, setTouched } =
      useFormikContext();

    useEffect(() => {
      (async () => {
        const validationErrors = await validateForm();
        if (Object.keys(validationErrors).length > 0) {
          setTouched(setNestedObjectValues(validationErrors, true));
        }
      })();
    }, [values, initialValues]);

    return null;
  };

  return (
    <Box style={{ maxWidth: '75vw' }}>
      <Formik
        initialValues={{
          controls: controlActions.getControls(),
        }}
        validationSchema={validationSchema}
        enableReinitialize={true}
      >
        {(formik) => (
          <form onSubmit={formik.handleSubmit}>
            <FormObserver />
            <TableContainer component={Paper}>
              <FormControl fullWidth margin="normal">
                <FieldArray
                  name="controls"
                  validateOnChange
                  render={(arrayHelpers) => (
                    <>
                      <Table size='small' style={{ width: '50vw' }}>
                        <TableHead>
                          <TableRow>
                            <TableCell align="center">Well</TableCell>
                            <TableCell align="center">Control</TableCell>
                            <TableCell align="center">Control Primer</TableCell>
                            <TableCell />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {formik.values.controls
                            ? formik.values.controls.map((control, index) => (
                                <TableRow key={index}>
                                  <TableCell style={{ textAlign: 'center' }}>
                                    {getWellName(control.well)}
                                  </TableCell>
                                  <TableCell>
                                      <DebounceInput
                                        debounceTimeout={500}
                                        minLength={1}
                                        element={TextField}
                                        required
                                        variant="outlined"
                                        size="small"
                                        margin="none"
                                        id="name"
                                        label="Name"
                                        fullWidth
                                        {...formik.getFieldProps(
                                          `controls[${index}].name`
                                        )}
                                        onChange={(e) => {
                                          controlActions.updateName(
                                            control._id,
                                            e.target.value
                                          );
                                          formik.handleChange(e);
                                        }}
                                      />
                                    <ErrorMessage
                                      name={`controls[${index}].name`}
                                    />
                                  </TableCell>
                                  <TableCell>
                                      <DebounceInput
                                        debounceTimeout={500}
                                        minLength={1}
                                        element={TextField}
                                        required
                                        variant="outlined"
                                        id="primer"
                                        label="Primer"
                                        size="small"
                                        margin="none"
                                        fullWidth
                                        {...formik.getFieldProps(
                                          `controls[${index}].controlPrimerName`
                                        )}
                                        onChange={(e) => {
                                          controlActions.updateControlPrimerName(
                                            control._id,
                                            e.target.value
                                          );
                                          formik.handleChange(e);
                                        }}
                                      />
                                    <ErrorMessage
                                      name={`controls[${index}].controlPrimerName`}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <Button
                                      className="remove-event"
                                      onClick={() => {
                                        controlActions.deleteControl(
                                          control._id
                                        );
                                      }}
                                    >
                                      <RemoveCircleOutlineIcon />
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))
                            : null}
                        </TableBody>
                      </Table>
                      <Button
                        className="add-control"
                        variant="outlined"
                        onClick={controlActions.createControl}
                        disabled={!controlActions.canAddControl()}
                        fullWidth
                      >
                        Add Control
                      </Button>
                    </>
                  )}
                />
                {typeof formik.errors.tournamentEvents === 'string' ? (
                  <div style={{ color: 'red', marginBottom: '20px' }}>
                    {formik.errors.tournamentEvents}
                  </div>
                ) : (
                  <div style={{ color: 'red', marginBottom: '20px' }} />
                )}
              </FormControl>
            </TableContainer>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default ControlTable;
